
const libPictView = require('pict-view');
// The same pict-section-recordset association widget the Project view uses for its "Observation Types"
// tab — embedded here for the OTHER direction (the projects that use this type), anchored on the
// manifest's Name. One association, both sides: that consistency is the point.
const libRecordSetAssociationEditor = require('pict-section-recordset/source/views/associate/RecordSet-AssociationEditor.js');

/**
 * Observation Type editor — the bespoke record page for an ObservationManifest.
 *
 * The pict-section-recordset list stays the browse surface; this view is the create / edit / clone
 * experience. It is a thin shell: all state lives in pict.AppData.ObservationTypeEditor and all
 * behavior in the ObservationTypeEditor provider, which the routes drive (newType / loadAsync /
 * cloneFrom) before rendering. The view derives + repaints declaratively.
 */
class ObservationTypeEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ObservationTypeEditor;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		// The main render replaces the whole editor container (and with it the empty Projects host), so
		// (re)paint the embedded association editor into it after every paint when a saved type is shown.
		this._renderProjectsAssociation();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/**
	 * Lazily register (once) a dedicated association-editor instance for the manifest side of the
	 * ProjectObservationManifest association. Mirrors how the PSRS read view registers an Association
	 * tab editor, but anchored on ObservationManifest (so it lists the PROJECTS using this type).
	 * AllowSynthesize is false because the defaults synthesizer is a project-side operation.
	 * @return {any|null} the registered view, or null if the association manager / association is absent.
	 */
	_ensureProjectsEditorView()
	{
		const tmpHash = 'OTE-ProjectsAssocEditor';
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		const tmpManager = this.pict.providers.RecordSetAssociationManager;
		if (!tmpManager || !tmpManager.getAssociation('ProjectObservationManifest')) { return null; }
		this.pict.addView(tmpHash, Object.assign({}, libRecordSetAssociationEditor.default_configuration,
			{
				ViewIdentifier: tmpHash,
				AssociationHash: 'ProjectObservationManifest',
				ThisRecordSet: 'ObservationManifest',
				DefaultDestinationAddress: '#OTE-ProjectsAssoc',
				PickerMode: 'single',
				AllowSynthesize: false,
			}), libRecordSetAssociationEditor);
		return this.pict.views[tmpHash];
	}

	/**
	 * Paint the embedded "Projects using this type" association editor into its host, anchored on the
	 * saved manifest's Name. No-op when there's no saved type (the host is absent — the template shows a
	 * "save first" hint instead). Renders are serialized so rapid OT-editor repaints don't race the
	 * association editor's async load.
	 */
	_renderProjectsAssociation()
	{
		const tmpProvider = this.provider;
		if (!tmpProvider) { return; }
		const tmpAnchor = tmpProvider.projectsAnchorName;
		const tmpHost = this.pict.ContentAssignment.getElement('#OTE-ProjectsAssoc');
		if (!tmpAnchor || !tmpHost || (tmpHost.length === 0)) { return; }
		const tmpEditor = this._ensureProjectsEditorView();
		if (!tmpEditor) { return; }
		tmpEditor.options.ThisID = tmpAnchor;
		tmpEditor.options.DefaultDestinationAddress = '#OTE-ProjectsAssoc';
		this._projectsRenderQueue = (this._projectsRenderQueue || Promise.resolve())
			.then(() => tmpEditor.renderEditor())
			.catch((pError) => { this.pict.log.warn(`ObservationTypeEditor: projects association render failed: ${pError && (pError.message || pError)}`); });
	}
}

module.exports = ObservationTypeEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'ObservationTypeEditor',
	DefaultRenderable: 'ObservationTypeEditorWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.ote-wrap { padding: 1.25rem 1.5rem 2rem; }
		.ote-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
		.ote-header-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
		.ote-scope-tag { vertical-align: middle; }
		.ote-meta { display: flex; gap: 1rem; flex-wrap: wrap; }
		.ote-meta .field { margin-bottom: 0; }
		.ote-meta .field.ote-name { flex: 1 1 16rem; }
		.ote-meta .field.ote-display { flex: 1 1 12rem; }
		.ote-meta .field.ote-category { flex: 0 1 12rem; }
		.ote-section-head { display: flex; justify-content: space-between; align-items: center; margin: 1.5rem 0 0.5rem; }
		.ote-req-table td, .ote-req-table th { vertical-align: top; }
		.ote-req-table td { padding-top: 0.45rem; }
		.ote-req-artifact { min-width: 11rem; }
		.ote-req-summary { display: block; font-size: 0.72rem; color: var(--bulma-text-weak, #7a7a7a); margin-top: 0.15rem; }
		.ote-preview { background: var(--bulma-scheme-main-bis, #fafafa); }
		.ote-pv-req { padding: 0.5rem 0; border-bottom: 1px solid var(--bulma-border-weak, #f0f0f0); }
		.ote-pv-req:last-child { border-bottom: none; }
		.ote-pv-reqhead { margin-bottom: 0.35rem; }
		.ote-pv-field { margin: 0.3rem 0; max-width: 28rem; }
		.ote-pv-fieldlabel { display: block; font-size: 0.72rem; color: var(--bulma-text-weak, #7a7a7a); }
		.ote-pv-sheet { overflow-x: auto; }
		.ote-pv-sheet th { white-space: nowrap; font-size: 0.8rem; }
		.ote-pv-sheet input.input { min-width: 6rem; }
		.ote-req-num { width: 5.5rem; }
		.ote-req-missing { color: var(--bulma-danger, #cc0f35); }
		.ote-flavor-note { margin-top: 1.25rem; }
		.ote-projects-assoc { margin-top: 0.25rem; }
		.ote-empty { padding: 2.5rem 1rem; }
		.ote-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #485fc7); border-radius: 50%; animation: ote-spin 0.8s linear infinite; }
		@keyframes ote-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ObservationType-Wrap',
			Template: /*html*/`
				<div class="ote-wrap">
					<datalist id="ote-artifact-options">{~TS:ObservationType-ArtifactOption:AppData.ObservationTypeEditor.ArtifactOptions~}</datalist>
					<datalist id="ote-category-options">{~TS:ObservationType-CategoryOption:AppData.ObservationTypeEditor.Categories~}</datalist>
					{~TS:ObservationType-Message:AppData.ObservationTypeEditor.MessageSlot~}
					{~TS:ObservationType-Loading:AppData.ObservationTypeEditor.LoadingSlot~}
					{~TS:ObservationType-Editor:AppData.ObservationTypeEditor.EditorSlot~}
					{~TS:ObservationType-Empty:AppData.ObservationTypeEditor.EmptySlot~}
				</div>`
		},
		{
			Hash: 'ObservationType-ArtifactOption',
			Template: /*html*/`<option value="{~D:Record.Name~}"></option>`
		},
		{
			Hash: 'ObservationType-CategoryOption',
			Template: /*html*/`<option value="{~D:Record.Name~}"></option>`
		},
		{
			Hash: 'ObservationType-Editor',
			Template: /*html*/`
				<div class="ote-editor">
					<div class="ote-header">
						<div>
							<h1 class="title is-4">{~D:Record.TitleText~}</h1>
							<p class="subtitle is-6 has-text-grey">Reference / catalog observation type · <span class="tag is-light ote-scope-tag">{~D:Record.ScopeLabel~}</span></p>
						</div>
						<div class="ote-header-actions">
							<button class="button is-light" onclick="_Pict.providers.ObservationTypeEditor.backToList()"><span class="icon">{~I:ChevronLeft~}</span><span>Back</span></button>
							{~TS:ObservationType-CloneButton:AppData.ObservationTypeEditor.EditorSlot~}
							{~TS:ObservationType-SaveButton:AppData.ObservationTypeEditor.EditorSlot~}
						</div>
					</div>

					{~TS:ObservationType-ReadOnlyNotice:AppData.ObservationTypeEditor.ReadOnlyNoticeSlot~}
					{~TS:ObservationType-ClonedNotice:AppData.ObservationTypeEditor.ClonedNoticeSlot~}

					<div class="box">
						<div class="ote-meta">
							<div class="field ote-name">
								<label class="label is-small">Name <span class="has-text-grey has-text-weight-normal">(unique · used by project associations)</span></label>
								<div class="control"><input class="input" type="text" maxlength="30" value="{~D:Record.Name~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setManifestField('Name', this.value)" /></div>
							</div>
							<div class="field ote-display">
								<label class="label is-small">Display name</label>
								<div class="control"><input class="input" type="text" maxlength="30" value="{~D:Record.DisplayName~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setManifestField('DisplayName', this.value)" /></div>
							</div>
							<div class="field ote-category">
								<label class="label is-small">Category</label>
								<div class="control"><input class="input" type="text" list="ote-category-options" value="{~D:Record.Category~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setManifestField('Category', this.value)" /></div>
							</div>
						</div>
					</div>

					<div class="ote-section-head">
						<h2 class="title is-5">Requirements <span class="tag is-light">{~D:Record.RequirementCount~}</span></h2>
						{~TS:ObservationType-AddButton:AppData.ObservationTypeEditor.EditorSlot~}
					</div>
					<p class="help mb-2">Each requirement collects a number of one artifact type. Min/Max bound how many; mark Required to force at least one.</p>

					<table class="table is-fullwidth is-narrow is-hoverable ote-req-table">
						<thead><tr><th>Name</th><th>Artifact type</th><th class="has-text-centered">Required</th><th>Min</th><th>Max</th><th>Default (JSON)</th><th></th></tr></thead>
						<tbody>{~TS:ObservationType-RequirementRow:AppData.ObservationTypeEditor.Requirements~}</tbody>
					</table>
					{~TS:ObservationType-EmptyRequirements:AppData.ObservationTypeEditor.EmptyRequirementsSlot~}

					<div class="ote-section-head">
						<h2 class="title is-5">Preview</h2>
						<div class="buttons has-addons mb-0">
							<button class="button is-small {~D:Record.JournalBtnClass~}" onclick="_Pict.providers.ObservationTypeEditor.setPreviewMode('journal')"><span class="icon">{~I:FileText~}</span><span>Journal</span></button>
							<button class="button is-small {~D:Record.SpreadsheetBtnClass~}" onclick="_Pict.providers.ObservationTypeEditor.setPreviewMode('spreadsheet')"><span class="icon">{~I:Spreadsheet~}</span><span>Spreadsheet</span></button>
						</div>
					</div>
					<p class="help mb-2">How this type looks when filled in the field. The same type can be associated to a project as either a journal or a spreadsheet.</p>
					<div class="box ote-preview">
						{~TS:ObservationType-PreviewEmpty:AppData.ObservationTypeEditor.PreviewEmptySlot~}
						{~TS:ObservationType-PreviewJournal:AppData.ObservationTypeEditor.PreviewJournalSlot~}
						{~TS:ObservationType-PreviewSheet:AppData.ObservationTypeEditor.PreviewSpreadsheetSlot~}
					</div>

					<div class="ote-section-head">
						<h2 class="title is-5">Projects <span class="tag is-light">using this type</span></h2>
					</div>
					<p class="help mb-2">The projects that use this observation type. For each, choose whether it appears as a <strong>Journal</strong> or a <strong>Spreadsheet</strong> and set its order — the same type can be used both ways across different projects.</p>
					{~TS:ObservationType-Projects:AppData.ObservationTypeEditor.ProjectsSlot~}
					{~TS:ObservationType-ProjectsHint:AppData.ObservationTypeEditor.ProjectsHintSlot~}
				</div>`
		},
		{
			Hash: 'ObservationType-Projects',
			Template: /*html*/`<div id="OTE-ProjectsAssoc" class="ote-projects-assoc"></div>`
		},
		{
			Hash: 'ObservationType-ProjectsHint',
			Template: /*html*/`<article class="message is-light is-small"><div class="message-body">Save this observation type first — then you can associate it with projects here.</div></article>`
		},
		{
			Hash: 'ObservationType-SaveButton',
			Template: /*html*/`{~TIfAbs:ObservationType-SaveButton-Inner:Record:Record.CanEdit^TRUE^~}`
		},
		{
			Hash: 'ObservationType-SaveButton-Inner',
			Template: /*html*/`<button class="button is-link" {~D:Record.SaveDisabledAttr~} onclick="_Pict.providers.ObservationTypeEditor.saveAsync()"><span class="icon">{~I:Save~}</span><span>Save</span></button>`
		},
		{
			Hash: 'ObservationType-CloneButton',
			Template: /*html*/`{~TIfAbs:ObservationType-CloneButton-Inner:Record:Record.CanClone^TRUE^~}`
		},
		{
			Hash: 'ObservationType-CloneButton-Inner',
			Template: /*html*/`<button class="button is-light" onclick="_Pict.providers.ObservationTypeEditor.cloneCurrentToMyCustomer()"><span class="icon">{~I:Copy~}</span><span>Clone</span></button>`
		},
		{
			Hash: 'ObservationType-AddButton',
			Template: /*html*/`{~TIfAbs:ObservationType-AddButton-Inner:Record:Record.CanEdit^TRUE^~}`
		},
		{
			Hash: 'ObservationType-AddButton-Inner',
			Template: /*html*/`<button class="button is-small is-link is-light" onclick="_Pict.providers.ObservationTypeEditor.addRequirement()"><span class="icon">{~I:Plus~}</span><span>Add requirement</span></button>`
		},
		{
			Hash: 'ObservationType-RequirementRow',
			Template: /*html*/`
				<tr>
					<td><input class="input is-small" type="text" value="{~D:Record.Name~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementField('{~D:Record.Key~}','Name', this.value)" /></td>
					<td>
						<input class="input is-small ote-req-artifact{~NE:Record.ArtifactMissing^ ote-req-missing~}" type="text" list="ote-artifact-options" placeholder="pick an artifact type" value="{~D:Record.ArtifactName~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementArtifact('{~D:Record.Key~}', this.value)" />
						<span class="ote-req-summary">{~D:Record.ArtifactSummary~}</span>
					</td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.RequiredAttr~} {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementField('{~D:Record.Key~}','Required', this.checked)" /></td>
					<td><input class="input is-small ote-req-num" type="number" min="0" value="{~D:Record.CountMinimum~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementField('{~D:Record.Key~}','CountMinimum', this.value)" /></td>
					<td><input class="input is-small ote-req-num" type="number" min="0" value="{~D:Record.CountMaximum~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementField('{~D:Record.Key~}','CountMaximum', this.value)" /></td>
					<td><input class="input is-small" type="text" value="{~D:Record.DefaultJSON~}" {~D:Record.ReadOnlyAttr~} onchange="_Pict.providers.ObservationTypeEditor.setRequirementField('{~D:Record.Key~}','DefaultJSON', this.value)" /></td>
					<td class="has-text-right">{~TIfAbs:ObservationType-RemoveButton:Record:AppData.ObservationTypeEditor.CanEdit^TRUE^~}</td>
				</tr>`
		},
		{
			Hash: 'ObservationType-RemoveButton',
			Template: /*html*/`<button class="button is-small is-white" title="Remove requirement" onclick="_Pict.providers.ObservationTypeEditor.removeRequirement('{~D:Record.Key~}')">{~I:Trash~}</button>`
		},
		{
			Hash: 'ObservationType-EmptyRequirements',
			Template: /*html*/`<p class="has-text-grey has-text-centered py-4">No requirements yet. Use <strong>Add requirement</strong> to compose this type.</p>`
		},
		{
			Hash: 'ObservationType-PreviewEmpty',
			Template: /*html*/`<p class="has-text-grey has-text-centered py-4">Add requirements with artifact types to preview the observation.</p>`
		},
		{
			Hash: 'ObservationType-PreviewJournal',
			Template: /*html*/`<div class="ote-pv-journal">{~TS:ObservationType-PreviewJournalReq:AppData.ObservationTypeEditor.PreviewJournal~}</div>`
		},
		{
			Hash: 'ObservationType-PreviewJournalReq',
			Template: /*html*/`
				<div class="ote-pv-req">
					<div class="ote-pv-reqhead"><strong>{~D:Record.Label~}</strong>{~TIfAbs:ObservationType-PvRequiredTag:Record:Record.RequiredFlag^TRUE^~}{~TS:ObservationType-PvRepeatTag:Record.RepeatSlot~}</div>
					{~TS:ObservationType-PreviewField:Record.Fields~}
					{~TS:ObservationType-PvCapture:Record.CaptureSlot~}
				</div>`
		},
		{
			Hash: 'ObservationType-PvRequiredTag',
			Template: /*html*/`<span class="tag is-danger is-light ml-2">required</span>`
		},
		{
			Hash: 'ObservationType-PvRepeatTag',
			Template: /*html*/`<span class="tag is-light ml-2">{~D:Record.Text~}</span>`
		},
		{
			Hash: 'ObservationType-PvCapture',
			Template: /*html*/`<div class="ote-pv-field has-text-grey"><span class="icon">{~I:Image~}</span> captures <strong>{~D:Record.ArtifactName~}</strong> <span class="is-size-7">(no editable fields)</span></div>`
		},
		{
			Hash: 'ObservationType-PreviewField',
			Template: /*html*/`<div class="ote-pv-field">{~TIfAbs:ObservationType-PvFieldLabel:Record:Record.MultiField^TRUE^~}{~TIfAbs:ObservationType-PreviewInputSelect:Record:Record.IsSelect^TRUE^~}{~TIfAbs:ObservationType-PreviewInputCheckbox:Record:Record.IsCheckbox^TRUE^~}{~TIfAbs:ObservationType-PreviewInputText:Record:Record.IsText^TRUE^~}</div>`
		},
		{
			Hash: 'ObservationType-PvFieldLabel',
			Template: /*html*/`<label class="ote-pv-fieldlabel">{~D:Record.Label~}</label>`
		},
		{
			Hash: 'ObservationType-PreviewInputSelect',
			Template: /*html*/`<span class="select is-small is-fullwidth"><select disabled>{~TS:ObservationType-PreviewOption:Record.Options~}</select></span>`
		},
		{
			Hash: 'ObservationType-PreviewInputCheckbox',
			Template: /*html*/`<input type="checkbox" disabled />`
		},
		{
			Hash: 'ObservationType-PreviewInputText',
			Template: /*html*/`<input class="input is-small" type="{~D:Record.InputType~}" placeholder="{~D:Record.Label~}" disabled />`
		},
		{
			Hash: 'ObservationType-PreviewOption',
			Template: /*html*/`<option>{~D:Record.Value~}</option>`
		},
		{
			Hash: 'ObservationType-PreviewSheet',
			Template: /*html*/`
				<div class="ote-pv-sheet">
					<table class="table is-bordered is-narrow">
						<thead><tr>{~TS:ObservationType-PreviewCol:AppData.ObservationTypeEditor.PreviewColumns~}</tr></thead>
						<tbody>{~TS:ObservationType-PreviewRow:AppData.ObservationTypeEditor.PreviewRows~}</tbody>
					</table>
				</div>`
		},
		{
			Hash: 'ObservationType-PreviewCol',
			Template: /*html*/`<th>{~D:Record.Header~}</th>`
		},
		{
			Hash: 'ObservationType-PreviewRow',
			Template: /*html*/`<tr>{~TS:ObservationType-PreviewCell:Record.Cells~}</tr>`
		},
		{
			Hash: 'ObservationType-PreviewCell',
			Template: /*html*/`<td>{~TIfAbs:ObservationType-PreviewInputSelect:Record:Record.IsSelect^TRUE^~}{~TIfAbs:ObservationType-PreviewInputCheckbox:Record:Record.IsCheckbox^TRUE^~}{~TIfAbs:ObservationType-PreviewInputText:Record:Record.IsText^TRUE^~}</td>`
		},
		{
			Hash: 'ObservationType-ReadOnlyNotice',
			Template: /*html*/`
				<article class="message is-warning is-small">
					<div class="message-body">
						<span class="icon">{~I:Lock~}</span>
						This <strong>{~D:Record.ScopeLabel~}</strong> observation type is <strong>read-only</strong> here. Editing your organization’s own types needs the <em>Manage observation types</em> permission; shared platform defaults can be <strong>cloned</strong> to make an editable copy for your organization.
					</div>
				</article>`
		},
		{
			Hash: 'ObservationType-ClonedNotice',
			Template: /*html*/`<article class="message is-success is-small"><div class="message-body">Cloned from <strong>{~D:Record.ClonedFrom~}</strong>. Review the name and requirements, then Save to create it.</div></article>`
		},
		{
			Hash: 'ObservationType-Message',
			Template: /*html*/`<div class="notification {~D:Record.MessageClass~} is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'ObservationType-Loading',
			Template: /*html*/`<div class="ote-empty box has-text-grey has-text-centered"><span class="ote-spinner"></span><p class="mt-3">Loading…</p></div>`
		},
		{
			Hash: 'ObservationType-Empty',
			Template: /*html*/`<div class="ote-empty box has-text-grey has-text-centered"><span class="icon is-large">{~I:File~}</span><p class="mt-3">Pick an observation type from the list, or create a new one.</p></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'ObservationTypeEditorWrap',
			TemplateHash: 'ObservationType-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
